import { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Battery,
  Calculator,
  Cloud,
  Compass,
  Droplets,
  Eye,
  Flame,
  Gauge,
  GitBranch,
  Layers,
  Lightbulb,
  LucideIcon,
  Power,
  Radio,
  Signal,
  Sun,
  Target,
  Thermometer,
  Volume2,
  Waves,
  Wind,
  Zap,
} from "lucide-react";
import { getPreSignedUrlForDownload } from "../api/admin";

// Prefix used for custom uploaded parameter icons stored in S3
export const UPLOADED_ICON_PREFIX = "parameter-icons/";

export const isUploadedIcon = (icon?: string): boolean =>
  !!icon && icon.startsWith(UPLOADED_ICON_PREFIX);

export interface ParameterIconDefinition {
  name: string;
  label: string;
  Icon: LucideIcon;
}

// Curated icon set selectable in the parameter form
export const PARAMETER_ICONS: ParameterIconDefinition[] = [
  { name: "thermometer", label: "Thermometer", Icon: Thermometer },
  { name: "gauge", label: "Gauge", Icon: Gauge },
  { name: "zap", label: "Voltage", Icon: Zap },
  { name: "activity", label: "Activity", Icon: Activity },
  { name: "power", label: "Power", Icon: Power },
  { name: "battery", label: "Battery", Icon: Battery },
  { name: "radio", label: "Frequency", Icon: Radio },
  { name: "waves", label: "Humidity", Icon: Waves },
  { name: "cloud", label: "PM / Cloud", Icon: Cloud },
  { name: "wind", label: "Air / AQI", Icon: Wind },
  { name: "droplets", label: "Water", Icon: Droplets },
  { name: "sun", label: "Light", Icon: Sun },
  { name: "flame", label: "Fire", Icon: Flame },
  { name: "volume2", label: "Sound", Icon: Volume2 },
  { name: "lightbulb", label: "Lightbulb", Icon: Lightbulb },
  { name: "compass", label: "Direction", Icon: Compass },
  { name: "signal", label: "Signal", Icon: Signal },
  { name: "eye", label: "Visibility", Icon: Eye },
  { name: "layers", label: "Layers", Icon: Layers },
  { name: "barchart3", label: "Chart", Icon: BarChart3 },
  { name: "calculator", label: "Calculator", Icon: Calculator },
  { name: "gitbranch", label: "Branch", Icon: GitBranch },
  { name: "alerttriangle", label: "Warning", Icon: AlertTriangle },
  { name: "target", label: "Target", Icon: Target },
];

// Alternative names (parameter identifiers, common synonyms) → icon name
const ICON_ALIASES: Record<string, string> = {
  temperature: "thermometer",
  voltage: "zap",
  current: "activity",
  energy: "battery",
  frequency: "radio",
  humidity: "waves",
  chart: "barchart3",
  warning: "alerttriangle",
  "pm2.5": "cloud",
  pm10: "cloud",
  pm: "cloud",
  air: "wind",
  aqi: "wind",
  water: "droplets",
  light: "sun",
  lux: "sun",
  fire: "flame",
  sound: "volume2",
  noise: "volume2",
  direction: "compass",
  visibility: "eye",
};

const SUBSTRING_FALLBACKS: Array<[string, string]> = [
  ["voltage", "zap"],
  ["current", "activity"],
  ["power", "power"],
  ["energy", "battery"],
  ["frequency", "radio"],
  ["temp", "thermometer"],
  ["humidity", "waves"],
];

export function findIconDefinition(
  iconOrIdentifier?: string
): ParameterIconDefinition | undefined {
  if (!iconOrIdentifier) return undefined;
  const key = iconOrIdentifier.toLowerCase();
  const name = ICON_ALIASES[key] || key;
  const match = PARAMETER_ICONS.find((def) => def.name === name);
  if (match) return match;
  const fallback = SUBSTRING_FALLBACKS.find(([substr]) => key.includes(substr));
  if (fallback) return PARAMETER_ICONS.find((def) => def.name === fallback[1]);
  return undefined;
}

// Session cache of presigned download URLs for uploaded icons
const uploadedIconUrlCache = new Map<string, string>();

export function useUploadedIconUrl(iconKey?: string): string | undefined {
  const [url, setUrl] = useState<string | undefined>(
    iconKey ? uploadedIconUrlCache.get(iconKey) : undefined
  );

  useEffect(() => {
    if (!iconKey) {
      setUrl(undefined);
      return;
    }
    const cached = uploadedIconUrlCache.get(iconKey);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    getPreSignedUrlForDownload({ key: iconKey })
      .then((res) => {
        uploadedIconUrlCache.set(iconKey, res.url);
        if (!cancelled) setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setUrl(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [iconKey]);

  return url;
}

export function UploadedParameterIcon({
  iconKey,
  className = "w-4 h-4",
}: {
  iconKey: string;
  className?: string;
}) {
  const url = useUploadedIconUrl(iconKey);
  if (!url) return <Activity className={className} />;
  return (
    <img
      src={url}
      alt="parameter icon"
      className={`${className} rounded-sm object-contain`}
    />
  );
}

// Get icon element based on parameter icon string (named, uploaded, or identifier fallback)
export function getParameterIcon(iconOrIdentifier: string) {
  if (isUploadedIcon(iconOrIdentifier)) {
    return <UploadedParameterIcon iconKey={iconOrIdentifier} />;
  }
  const def = findIconDefinition(iconOrIdentifier);
  const Icon = def?.Icon || Activity;
  return <Icon className="w-4 h-4" />;
}
